import {
    Dropdown,
    Option,
    OptionGroup,
    type OptionOnSelectData,
    type SelectionEvents,
} from '@fluentui/react-components';
import type { Column } from '@tanstack/react-table';
import { type Dispatch, type SetStateAction, useCallback } from 'react';
import { useSharedStyles } from '../../shared-styles.js';
import { typedSetStringArrayColumnFilterValue } from '../filter/index.js';
import { STRING_ARRAY_FILTER_OPERATION, type StringArrayFilterValuesWithoutInArray } from '../filter/operation.js';
import type { StringArrayFilterSchemaWithoutInArray } from '../filter/schema.js';
import { DROPDOWN_STRING_ARRAY_OPTIONS } from './data.js';

type StringArrayOperationProps<TData, TValue> = {
    column: Column<TData, TValue>;
    dropdownValue: StringArrayFilterValuesWithoutInArray['id'];
    resetListFilter: (resetDropdown: boolean) => void;
    setDropdownValue: Dispatch<SetStateAction<StringArrayFilterValuesWithoutInArray['id']>>;
};

export function StringArrayOperation<TData, TValue>({
    column,
    dropdownValue,
    resetListFilter,
    setDropdownValue,
}: StringArrayOperationProps<TData, TValue>) {
    const sharedStyles = useSharedStyles();

    const onDropdownChange = useCallback(
        (_: SelectionEvents, changeData: OptionOnSelectData) => {
            const operation = changeData.selectedOptions[0] as typeof dropdownValue;

            if (
                dropdownValue ===
                    (STRING_ARRAY_FILTER_OPERATION.inArray.id as StringArrayFilterValuesWithoutInArray['id']) &&
                operation !== (STRING_ARRAY_FILTER_OPERATION.inArray.id as StringArrayFilterValuesWithoutInArray['id'])
            ) {
                resetListFilter(false);
            }

            setDropdownValue(operation);

            typedSetStringArrayColumnFilterValue(column, {
                ...(column.getFilterValue() as StringArrayFilterSchemaWithoutInArray | undefined),
                operation,
            });
        },
        [column, dropdownValue, resetListFilter, setDropdownValue]
    );

    return (
        <Dropdown
            className={sharedStyles.dropdown}
            value={STRING_ARRAY_FILTER_OPERATION[dropdownValue].label}
            selectedOptions={[dropdownValue]}
            onOptionSelect={onDropdownChange}
            listbox={{
                className: sharedStyles.dropdownListbox,
            }}
        >
            {DROPDOWN_STRING_ARRAY_OPTIONS.map((optionGroup, index) => (
                <OptionGroup key={index}>
                    {optionGroup.map((option) => {
                        return (
                            <Option key={option} value={option} text={STRING_ARRAY_FILTER_OPERATION[option].label}>
                                {STRING_ARRAY_FILTER_OPERATION[option].label}
                            </Option>
                        );
                    })}
                </OptionGroup>
            ))}
        </Dropdown>
    );
}
